/**
 * Home Screen – MetaTrader Style Drawer + TradingView Quotes
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  Modal,
  Animated,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { WebView } from 'react-native-webview';
import Icon from '../components/Icon';
import { authAPI } from '../services/authApi';
import { useTheme } from '../context/ThemeContext';
import { wp, vs, spacing, WINDOW_WIDTH } from '../utils/responsive';

/* ================= HOME ================= */

const HomeScreen = () => {
  const { colors, isDarkMode } = useTheme();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const DRAWER_WIDTH = 0.78;
  const slideAnim = useRef(
    new Animated.Value(-WINDOW_WIDTH * DRAWER_WIDTH)
  ).current;

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await authAPI.getProfile();
      setProfile(data);
    } catch {}
    setLoading(false);
  };

  const toggleDrawer = () => {
    Animated.timing(slideAnim, {
      toValue: drawerVisible ? -WINDOW_WIDTH * DRAWER_WIDTH : 0,
      duration: 280,
      useNativeDriver: true,
    }).start(() => setDrawerVisible(!drawerVisible));
  };

  const showReadOnlyAlert = () => {
    Alert.alert(
      'Read Only',
      'This feature is read only',
      [{ text: 'OK' }],
      { cancelable: true }
    );
  };

  const menuItems = [
    { icon: 'trending-up-outline', label: 'Trade' },
    { icon: 'newspaper-outline', label: 'News' },
    { icon: 'mail-outline', label: 'Mailbox' },
    { icon: 'calendar-outline', label: 'Journal' },
    { icon: 'settings-outline', label: 'Settings' },
    { icon: 'calendar-outline', label: 'Economic calendar', badge: 'Ads' },
    { icon: 'people-outline', label: 'Traders Community' },
    { icon: 'code-slash-outline', label: 'MQL5 Algo Trading' },
    { icon: 'help-circle-outline', label: 'User guide' },
    { icon: 'information-circle-outline', label: 'About' },
  ];

  /* ================= TRADINGVIEW HTML ================= */

  const tradingViewHTML = `
<!DOCTYPE html>
<html style="height:100%">
<head>
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<style>
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  background: #000;
  overflow: hidden;
}
.tradingview-widget-container,
.tradingview-widget-container__widget {
  width: 100%;
  height: 100%;
}
</style>
</head>
<body>
<div class="tradingview-widget-container">
  <div class="tradingview-widget-container__widget"></div>
  <script type="text/javascript"
    src="https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js"
    async>
  {
    "colorTheme": "dark",
    "dateRange": "12M",
    "locale": "en",
    "showFloatingTooltip": false,
    "showChart": false,
    "width": "100%",
    "height": "100%",
    "symbolActiveColor": "rgba(41, 98, 255, 0.12)",
    "tabs": [
      {
        "title": "Indices",
        "symbols": [
          { "s": "FOREXCOM:SPXUSD", "d": "S&P 500" },
          { "s": "FOREXCOM:NSXUSD", "d": "US 100" },
          { "s": "FOREXCOM:DJI", "d": "Dow Jones" },
          { "s": "INDEX:NKY", "d": "Japan 225" },
          { "s": "INDEX:DEU40", "d": "DAX" },
          { "s": "FOREXCOM:UKXGBP", "d": "FTSE 100" },
          { "s": "BINANCE:BTCUSDT", "d": "BTCUSDT" },
          { "s": "FOREXCOM:XAUUSD", "d": "Gold" },
          { "s": "OANDA:XAGUSD", "d": "Silver" },
          { "s": "CRYPTOCAP:CROID", "d": "Crude Oil" },
          { "s": "FX:EURUSD", "d": "EURUSD" },
          { "s": "OANDA:USDJPY", "d": "USDJPY" }
        ]
      },
      {
        "title": "Forex",
        "symbols": [
          { "s": "FX:EURUSD", "d": "EUR/USD" },
          { "s": "FX:GBPUSD", "d": "GBP/USD" },
          { "s": "FX:USDJPY", "d": "USD/JPY" },
          { "s": "FX:AUDUSD", "d": "AUD/USD" },
          { "s": "FX:USDCAD", "d": "USD/CAD" }
        ]
      }
    ]
  }
  </script>
</div>
</body>
</html>
`;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />

      {/* HEADER */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <TouchableOpacity onPress={toggleDrawer}>
            <Icon name="menu" size={22} color={colors.primary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.text }]}>
            Quotes
          </Text>
        </View>

        <View style={{ flexDirection: 'row', gap: 14 }}>
          <TouchableOpacity onPress={showReadOnlyAlert}>
            <Icon name="add" size={22} color={colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={showReadOnlyAlert}>
            <Icon name="create-outline" size={22} color={colors.primary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* QUOTES */}
      <View style={{ flex: 1 }}>
        <WebView
          source={{ html: tradingViewHTML }}
          originWhitelist={['*']}
          javaScriptEnabled
          domStorageEnabled
          scrollEnabled={false}
          bounces={false}
          style={{ flex: 1, backgroundColor: '#000' }}
        />
      </View>

      {/* DRAWER */}
      <Modal visible={drawerVisible} transparent animationType="none">
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={toggleDrawer}>
          <Animated.View
            style={[
              styles.drawer,
              {
                backgroundColor: colors.cardBackground,
                transform: [{ translateX: slideAnim }],
              },
            ]}
          >
            <View style={[styles.profileRow, { borderBottomColor: colors.border }]}>
              <View style={[styles.avatar, { borderColor: colors.primary }]} />
              <View>
                {loading ? (
                  <ActivityIndicator color={colors.primary} />
                ) : (
                  <>
                    <Text style={[styles.name, { color: colors.text }]}>
                      {profile?.name || 'Aakash Pokale'}
                    </Text>
                    <Text style={styles.server}>
                      1000077 – fizmoFXMarket-Server
                    </Text>
                  </>
                )}
              </View>
            </View>

            {menuItems.map((item, index) => (
              <View key={index} style={styles.menuItem}>
                <Icon name={item.icon} size={20} color={colors.textSecondary} />
                <Text style={[styles.menuText, { color: colors.text }]}>
                  {item.label}
                </Text>
              </View>
            ))}
          </Animated.View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: { flex: 1 },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.base,
    paddingVertical: vs(14),
    borderBottomWidth: 1,
  },

  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },

  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },

  drawer: {
    width: wp('78%'),
    height: '100%',
  },

  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 18,
    borderBottomWidth: 1,
    gap: 14,
  },

  avatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    borderWidth: 2,
  },

  name: { fontSize: 15, fontWeight: '600' },
  server: { fontSize: 12, color: '#888' },

  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 18,
  },

  menuText: {
    fontSize: 15,
  },
});

export default HomeScreen;
